import { request } from "./api";
import type { UserMe } from "@/types";

export type { UserMe };

let getMeInFlight: Promise<UserMe> | null = null;

function setToken(token: string) {
  document.cookie = `token=${token}; path=/; max-age=${30 * 24 * 60 * 60}; SameSite=Lax`;
}

function clearToken() {
  document.cookie = "token=; path=/; max-age=0";
}

function clearAllAppStorage() {
  if (typeof window === "undefined") return;
  try {
    const envPrefix = process.env.NEXT_PUBLIC_ENVIRONMENT ?? "app";
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const shouldRemove = 
          key.startsWith(envPrefix) ||
          key.startsWith("cajugram") ||
          key.startsWith("estoque-") ||
          key === "theme";
        if (shouldRemove) {
          keysToRemove.push(key);
        }
      }
    }
    
    keysToRemove.forEach(k => localStorage.removeItem(k));
  } catch {
    /* ignore quota/private mode */
  }
}

export const authService = {
  async login(
    loginIdentifier: string,
    password: string,
  ): Promise<{ promptSapiensLink: boolean; promptSalesforceLink: boolean }> {
    getMeInFlight = null;
    const data = await request<{
      access_token: string;
      prompt_sapiens_link?: boolean;
      prompt_salesforce_link?: boolean;
    }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ login_identifier: loginIdentifier, password }),
    });
    setToken(data.access_token);
    return {
      promptSapiensLink: data.prompt_sapiens_link || false,
      promptSalesforceLink: data.prompt_salesforce_link || false,
    };
  },

  async linkSapiens(username: string, password: string): Promise<void> {
    getMeInFlight = null;
    const data = await request<{ access_token: string }>("/auth/link-sapiens", {
      method: "POST",
      body: JSON.stringify({ sapiens_username: username, sapiens_password: password }),
    });
    setToken(data.access_token);
  },

  async linkSalesforce(email: string, password: string): Promise<void> {
    getMeInFlight = null;
    const data = await request<{ access_token: string }>("/auth/link-salesforce", {
      method: "POST",
      body: JSON.stringify({ salesforce_email: email, salesforce_password: password }),
    });
    setToken(data.access_token);
  },

  async updateProfile(fullName: string): Promise<UserMe> {
    getMeInFlight = null;
    return request<UserMe>("/auth/me", {
      method: "PATCH",
      body: JSON.stringify({ full_name: fullName }),
    });
  },

  async getMe(): Promise<UserMe> {
    if (getMeInFlight) return getMeInFlight;
    getMeInFlight = request<UserMe>("/auth/me").finally(() => {
      getMeInFlight = null;
    });
    return getMeInFlight;
  },

  async logout(): Promise<void> {
    const token = document.cookie
      .split("; ")
      .find((c) => c.startsWith("token="))
      ?.split("=")[1];
    if (token) {
      await request("/auth/logout", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    clearToken();
    clearAllAppStorage();
    window.location.href = "/login";
  },
};
